import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, Field, Input } from '@/design-system';
import { authApi, authProblem } from '@/entities/auth';
import { isApiError } from '@/shared/api';

import { AuthError, AuthHeading, AuthLayout, AuthLink, AuthNotice } from './AuthLayout';
import { authCopy } from './copy';
import { validateEmail, validateNewPassword } from './validation';

interface Errors {
    email?: string;
    password?: string;
    passwordConfirmation?: string;
}

/**
 * Wachtwoord herstellen: `/wachtwoord-herstellen?token=…&email=…`, de link uit
 * de mail van `POST /auth/forgot-password`.
 *
 * Na het opslaan log je gewoon in; de API geeft hier bewust geen token terug.
 */
export function ResetPasswordPage() {
    const [search] = useSearchParams();
    const token = search.get('token') ?? '';

    const [email, setEmail] = useState(search.get('email') ?? '');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [errors, setErrors] = useState<Errors>({});
    const [formError, setFormError] = useState<{ message: string; offline: boolean } | null>(null);
    const [invalidLink, setInvalidLink] = useState(token === '');
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();

        const emailError = validateEmail(email);
        const next: Errors = {
            ...(emailError ? { email: emailError } : {}),
            ...validateNewPassword(password, confirmation),
        };

        setErrors(next);
        setFormError(null);

        if (next.email || next.password || next.passwordConfirmation) {
            return;
        }

        setBusy(true);

        try {
            await authApi.resetPassword({
                token,
                email,
                password,
                passwordConfirmation: confirmation,
            });
            setDone(true);
        } catch (caught) {
            const problem = authProblem(caught);

            if (problem === 'invalid-reset-token') {
                setInvalidLink(true);
            } else if (problem === 'validation' && isApiError(caught)) {
                const fields: Errors = {
                    ...optional('email', caught.firstValidationError('email')),
                    ...optional('password', caught.firstValidationError('password')),
                    ...optional(
                        'passwordConfirmation',
                        caught.firstValidationError('password_confirmation'),
                    ),
                };

                setErrors(fields);

                if (caught.firstValidationError('token')) {
                    setInvalidLink(true);
                } else if (!fields.email && !fields.password && !fields.passwordConfirmation) {
                    setFormError({ message: authCopy.resetFailed, offline: false });
                }
            } else {
                setFormError({
                    message: problem === 'offline' ? authCopy.offline : authCopy.resetFailed,
                    offline: problem === 'offline',
                });
            }
        } finally {
            setBusy(false);
        }
    }

    const loginLink = email.trim()
        ? `${paths.login}?email=${encodeURIComponent(email.trim())}`
        : paths.login;
    const forgotLink = email.trim()
        ? `${paths.forgotPassword}?email=${encodeURIComponent(email.trim())}`
        : paths.forgotPassword;

    if (done) {
        return (
            <AuthLayout>
                <AuthHeading title={authCopy.resetTitle} />
                <AuthNotice>{authCopy.resetDone}</AuthNotice>
                <div className="flex justify-start">
                    <AuthLink to={loginLink}>{authCopy.goToLogin}</AuthLink>
                </div>
            </AuthLayout>
        );
    }

    if (invalidLink) {
        return (
            <AuthLayout>
                <AuthHeading
                    title={authCopy.resetTitle}
                    body={token === '' ? authCopy.resetIncomplete : authCopy.resetInvalid}
                />
                <div className="flex flex-col items-start gap-1">
                    <AuthLink to={forgotLink}>{authCopy.requestNewLink}</AuthLink>
                    <AuthLink to={loginLink}>{authCopy.backToLogin}</AuthLink>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <AuthHeading title={authCopy.resetTitle} body={authCopy.resetBody} />

            <form noValidate className="flex flex-col gap-3" onSubmit={handleSubmit}>
                <Field label={authCopy.email} labelCase="caps" size="lg" error={errors.email}>
                    <Input
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </Field>

                <Field
                    label={authCopy.newPassword}
                    labelCase="caps"
                    size="lg"
                    error={errors.password}
                >
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                </Field>

                <Field
                    label={authCopy.confirmPassword}
                    labelCase="caps"
                    size="lg"
                    error={errors.passwordConfirmation}
                >
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={confirmation}
                        onChange={(event) => setConfirmation(event.target.value)}
                    />
                </Field>

                {formError ? (
                    <AuthError tone={formError.offline ? 'offline' : 'danger'}>
                        {formError.message}
                    </AuthError>
                ) : null}

                <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
                    {busy ? authCopy.resetBusy : authCopy.resetSubmit}
                </Button>

                <div className="flex justify-start">
                    <AuthLink to={loginLink}>{authCopy.backToLogin}</AuthLink>
                </div>
            </form>
        </AuthLayout>
    );
}

function optional<K extends keyof Errors>(key: K, value: string | undefined): Errors {
    return value ? { [key]: value } : {};
}
